// Mantissa/exponent numbers keep the economy usable long after Number.MAX_SAFE_INTEGER.
// The UI only needs a small, positive subset for now, but the representation is
// deliberately ready for the rank ladder's 1e30 ceiling.
export function normalize(mantissa, exponent = 0) {
  if (!Number.isFinite(mantissa) || mantissa === 0) return { m: 0, e: 0 };
  const sign = Math.sign(mantissa);
  const magnitude = Math.abs(mantissa);
  const shift = Math.floor(Math.log10(magnitude));
  return { m: sign * (magnitude / (10 ** shift)), e: exponent + shift };
}

export function fromNumber(value) {
  return normalize(value);
}

export function fromString(value) {
  const match = String(value).trim().match(/^([+-]?[\d.]+)(?:e([+-]?\d+))?$/i);
  if (!match) throw new Error(`Invalid big number: ${value}`);
  return normalize(Number(match[1]), Number(match[2] || 0));
}

export function clone(value) {
  return { m: value.m, e: value.e };
}

export function add(left, right) {
  if (left.m === 0) return clone(right);
  if (right.m === 0) return clone(left);
  const difference = left.e - right.e;
  if (difference > 15) return clone(left);
  if (difference < -15) return clone(right);
  const exponent = Math.max(left.e, right.e);
  return normalize(
    left.m * (10 ** (left.e - exponent)) + right.m * (10 ** (right.e - exponent)),
    exponent,
  );
}

export function subtract(left, right) {
  return add(left, { m: -right.m, e: right.e });
}

export function multiply(left, right) {
  if (typeof right === 'number') return normalize(left.m * right, left.e);
  return normalize(left.m * right.m, left.e + right.e);
}

export function divide(left, right) {
  if (typeof right === 'number') return normalize(left.m / right, left.e);
  return normalize(left.m / right.m, left.e - right.e);
}

export function compare(left, right) {
  if (left.m === 0 && right.m === 0) return 0;
  if (left.m >= 0 && right.m < 0) return 1;
  if (left.m < 0 && right.m >= 0) return -1;
  if (left.e !== right.e) return (left.e > right.e ? 1 : -1) * Math.sign(left.m || right.m);
  return Math.sign(left.m - right.m);
}

export function isZero(value) {
  return value.m === 0;
}

export function toNumber(value) {
  if (value.e > 308) return Infinity;
  return value.m * (10 ** value.e);
}

export function format(value, decimals = 2) {
  if (!value || value.m === 0) return '0';
  const absoluteExponent = value.e;
  if (absoluteExponent < 3 && absoluteExponent > -3) {
    const number = toNumber(value);
    return number.toFixed(number < 10 && number % 1 !== 0 ? decimals : 0);
  }
  const suffixes = [
    [3, 'K'], [6, 'M'], [9, 'B'], [12, 'T'], [15, 'Qa'], [18, 'Qi'],
    [21, 'Sx'], [24, 'Sp'], [27, 'Oc'], [30, 'No'], [33, 'Dc'],
  ];
  const suffix = suffixes.find(([exponent]) => absoluteExponent < exponent + 3);
  if (suffix) {
    const scaled = value.m * (10 ** (absoluteExponent - suffix[0]));
    return `${scaled.toFixed(scaled < 10 ? decimals : 1)}${suffix[1]}`;
  }
  return `${value.m.toFixed(decimals)}e${absoluteExponent}`;
}

export function serialize(value) {
  return { m: value.m, e: value.e };
}
